// EmailGate — a lightweight popup that asks for the visitor's email so we
// can attribute their like/share/comment back to a known identity (no
// password, no session).
//
// Hooks into the global event bus:
//   • window.dispatchEvent(new CustomEvent("docm:request-identity", { detail: { reason, onResolved } }))
//   • onResolved(identity | null) fires when the modal closes.
//
// If the email matches a row in the database, we return the full identity
// (name + country + city) and welcome the user back. Otherwise we offer a
// minimal name/country form so they can still participate.

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Mail, Loader2, X as XIcon, Sparkles, CalendarCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { COUNTRIES_LIST } from "@/data/docm";
import { saveIdentity, lookupIdentity, loadIdentity } from "@/lib/identity";

function formatDeclaredDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

export default function EmailGate() {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const resolverRef = useRef(null);
  const inputRef = useRef(null);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [looking, setLooking] = useState(false);
  const [stage, setStage] = useState("email"); // "email" | "minimal" | "welcome"
  const [welcomeName, setWelcomeName] = useState("");
  const [declaredOn, setDeclaredOn] = useState("");

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      const existing = loadIdentity();
      // Allow callers to force-open the gate even when an identity exists.
      // We use this for the "Sign in" navbar affordance / switch-account flows.
      if (!detail.force && existing && existing.email) {
        // Already known — resolve immediately.
        if (typeof detail.onResolved === "function") detail.onResolved(existing);
        return;
      }
      resolverRef.current = typeof detail.onResolved === "function" ? detail.onResolved : null;
      setReason(detail.reason || "");
      setEmail("");
      setName("");
      setCountry("");
      setStage("email");
      setWelcomeName("");
      setDeclaredOn("");
      setOpen(true);
    };
    window.addEventListener("docm:request-identity", handler);
    return () => window.removeEventListener("docm:request-identity", handler);
  }, []);

  // Focus the email input ~200ms after the modal opens — gives the framer
  // animation time to settle. Replaces the unreliable `autoFocus` attribute
  // which sometimes raced with AnimatePresence and left the field unfocused.
  useEffect(() => {
    if (open && stage === "email") {
      const t = setTimeout(() => {
        try { inputRef.current?.focus(); } catch { /* noop */ }
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open, stage]);

  const close = (identity) => {
    setOpen(false);
    if (resolverRef.current) {
      try { resolverRef.current(identity || null); } catch { /* noop */ }
      resolverRef.current = null;
    }
  };

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    const e2 = email.trim().toLowerCase();
    if (!/.+@.+\..+/.test(e2)) return toast.error("Please enter a valid email.");
    setLooking(true);
    try {
      const result = await lookupIdentity(e2);
      if (result.found) {
        const ident = saveIdentity({
          email: e2,
          name: result.name || "",
          country: result.country || "",
          city: result.city || "",
          profession: result.profession || "",
          signatureRef: result.signature_ref || "",
          actRef: result.act_ref || "",
          declaredAt: result.declared_at || "",
        });
        setWelcomeName((result.name || "").split(" ")[0] || "friend");
        setDeclaredOn(formatDeclaredDate(result.declared_at));
        setStage("welcome");
        // Auto-close after a beat so the welcome state is felt.
        setTimeout(() => close(ident), 1600);
      } else {
        // Unknown email — collect a minimum profile so the user can participate.
        setStage("minimal");
      }
    } finally {
      setLooking(false);
    }
  };

  const submitMinimal = (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return toast.error("Please tell us your name.");
    if (!country) return toast.error("Please select your country.");
    const ident = saveIdentity({
      email: email.trim().toLowerCase(),
      name: name.trim(),
      country,
    });
    toast.success("Got it — you're signed in for this visit.");
    close(ident);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="email-gate"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop sits BEHIND the card (z-0) and closes on click. */}
          <div
            className="absolute inset-0 z-0 bg-docm-midnight/55 backdrop-blur-sm"
            onClick={() => close(null)}
            aria-hidden="true"
          />
          <motion.div
            className="relative z-10 w-full max-w-md bg-docm-paper rounded-3xl border border-docm-border shadow-[0_40px_120px_-30px_rgba(28,32,30,0.5)] p-7 md:p-9"
            initial={{ y: 24, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => close(null)}
              data-testid="email-gate-close"
              className="absolute top-4 right-4 p-1.5 rounded-full text-docm-inkSoft hover:text-docm-ink hover:bg-docm-linen transition-colors"
              aria-label="Close"
            >
              <XIcon size={16} />
            </button>

            {stage === "email" && (
              <form onSubmit={submitEmail}>
                <div className="w-12 h-12 rounded-2xl bg-docm-gold/15 text-docm-gold flex items-center justify-center mb-4">
                  <Mail size={20} />
                </div>
                <h3 className="font-serif-h text-xl md:text-2xl text-docm-ink leading-tight">
                  {reason ? reason : "Who's adding this?"}
                </h3>
                <p className="mt-2 text-[13.5px] text-docm-inkSoft leading-relaxed">
                  Enter your email — if you&rsquo;ve signed the Declaration or shared an act before,
                  we&rsquo;ll recognise you and skip the rest.
                </p>
                <div className="mt-5">
                  <Label className="text-[11.5px] uppercase tracking-[0.16em] text-docm-inkSoft font-semibold">Email</Label>
                  <Input
                    ref={inputRef}
                    data-testid="email-gate-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-1.5 h-12 bg-docm-paper border-docm-border text-docm-ink placeholder:text-docm-inkSoft/60 focus-visible:ring-docm-gold focus-visible:border-docm-gold rounded-xl"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="email-gate-continue"
                  disabled={looking}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-docm-moss text-docm-cream px-6 py-3 text-sm font-semibold hover:bg-docm-moss/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {looking ? <><Loader2 size={15} className="animate-spin" /> Checking…</> : "Continue"}
                </button>
                <p className="mt-3 text-[11.5px] text-docm-inkSoft/80 text-center">
                  We use this only to attribute your contribution — never sold, never shared.
                </p>
              </form>
            )}

            {stage === "minimal" && (
              <form onSubmit={submitMinimal} data-testid="email-gate-minimal">
                <div className="w-12 h-12 rounded-2xl bg-docm-terracotta/15 text-docm-terracotta flex items-center justify-center mb-4">
                  <Sparkles size={20} />
                </div>
                <h3 className="font-serif-h text-xl md:text-2xl text-docm-ink leading-tight">First time? Welcome.</h3>
                <p className="mt-2 text-[13.5px] text-docm-inkSoft leading-relaxed">
                  We don&rsquo;t have you yet — tell us your name and country so your contribution carries
                  your voice.
                </p>
                <div className="mt-5 space-y-4">
                  <div>
                    <Label className="text-[11.5px] uppercase tracking-[0.16em] text-docm-inkSoft font-semibold">Name</Label>
                    <Input data-testid="email-gate-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                      className="mt-1.5 h-12 bg-docm-paper border-docm-border rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-[11.5px] uppercase tracking-[0.16em] text-docm-inkSoft font-semibold">Country</Label>
                    <Select value={country} onValueChange={setCountry}>
                      <SelectTrigger data-testid="email-gate-country" className="mt-1.5 h-12 bg-docm-paper border-docm-border rounded-xl">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {COUNTRIES_LIST.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <button type="submit" data-testid="email-gate-save"
                  className="mt-5 w-full inline-flex items-center justify-center rounded-full bg-docm-moss text-docm-cream px-6 py-3 text-sm font-semibold hover:bg-docm-moss/90 transition-colors">
                  Continue
                </button>
              </form>
            )}

            {stage === "welcome" && (
              <div data-testid="email-gate-welcome" className="text-center py-2">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-docm-moss/15 text-docm-moss flex items-center justify-center mb-3">
                  <Sparkles size={22} />
                </div>
                <h3 className="font-serif-h text-xl md:text-2xl text-docm-ink leading-tight">
                  Welcome back, <span className="font-display italic text-docm-moss">{welcomeName}</span>.
                </h3>
                <p className="mt-2 text-[13.5px] text-docm-inkSoft leading-relaxed">
                  Picking up where you left off.
                </p>
                {declaredOn && (
                  <div data-testid="email-gate-declared-on" className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-docm-gold/10 border border-docm-gold/30 text-docm-gold text-[12px] font-semibold">
                    <CalendarCheck size={13} />
                    Declared on {declaredOn}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
